import { FC } from 'react';
import { Link } from 'react-router-dom';
import {
  TherapistStats,
  TodaySchedule,
  UpcomingBookings,
} from '../../../components/dashboard/therapist';
import { QuickActions } from './QuickActions';
import type {
  TherapistDashboardSection,
  TherapistDashboardStats,
  TherapistProfile,
  TherapistAppointment,
} from '../../../types/therapist-dashboard.types';

interface OverviewSectionProps {
  userName: string | undefined;
  stats: TherapistDashboardStats | undefined;
  profile: TherapistProfile | undefined;
  statsLoading: boolean;
  profileLoading: boolean;
  appointmentsLoading: boolean;
  upcomingAppointments: TherapistAppointment[];
  onSectionChange: (section: TherapistDashboardSection) => void;
  onUpdateStatus: (appointmentId: string, status: 'completed' | 'no_show' | 'cancelled') => void;
}

export const OverviewSection: FC<OverviewSectionProps> = ({
  userName,
  stats,
  profile,
  statsLoading,
  profileLoading,
  appointmentsLoading,
  upcomingAppointments,
  onSectionChange,
  onUpdateStatus,
}) => {
  return (
    <div className="space-y-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 rounded-lg p-6 md:p-8 text-white shadow-lg dark:shadow-primary-900/30">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">
          Welcome back, {userName?.split(' ')[0] || 'Therapist'}!
        </h1>
        <p className="text-primary-100 dark:text-primary-200 text-lg">
          {stats?.todayAppointments
            ? `You have ${stats.todayAppointments} appointment${stats.todayAppointments !== 1 ? 's' : ''} today`
            : 'No appointments scheduled for today'}
        </p>
      </div>

      {/* Stats */}
      <TherapistStats
        stats={stats}
        profile={profile}
        isLoading={statsLoading || profileLoading}
      />

      {/* Next Appointment */}
      {stats?.nextAppointment && (
        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border-2 border-primary-200 dark:border-primary-800 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">Next Appointment</h3>
              <p className="text-2xl font-bold text-primary-600 mb-1">
                {new Date(stats.nextAppointment.startTime).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </p>
              <p className="text-gray-700 dark:text-slate-300 mb-1">{stats.nextAppointment.serviceName}</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Client: {stats.nextAppointment.clientName}
              </p>
            </div>
            <Link
              to={`/therapist/appointments/${stats.nextAppointment.id}`}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-md hover:shadow-lg"
            >
              View Details
            </Link>
          </div>
        </div>
      )}

      {/* Today's Schedule */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Today's Schedule</h2>
          <button
            onClick={() => onSectionChange('schedule')}
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            View Full Schedule
          </button>
        </div>
        <TodaySchedule appointments={stats?.todaySchedule || []} isLoading={statsLoading} />
      </div>

      {/* Upcoming Bookings Preview */}
      {upcomingAppointments.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Upcoming Bookings</h2>
            <button
              onClick={() => onSectionChange('appointments')}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              View All
            </button>
          </div>
          <UpcomingBookings
            appointments={upcomingAppointments.slice(0, 3)}
            isLoading={appointmentsLoading}
            onUpdateStatus={onUpdateStatus}
          />
        </div>
      )}

      {/* Quick Actions */}
      <QuickActions onSectionChange={onSectionChange} />
    </div>
  );
};
